import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { ArrowUpRight, AlertTriangle } from "lucide-react"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

type IncreaseType = "cpi" | "pct" | "flat"

const NOTICE_OPTIONS = [
  { value: "30", label: "30 days" },
  { value: "60", label: "60 days" },
  { value: "90", label: "90 days" },
]

export default function RentIncreaseCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [currentRent, setCurrentRent] = useState("")
  const [increaseType, setIncreaseType] = useState<IncreaseType>("pct")
  const [increaseValue, setIncreaseValue] = useState("")
  const [rentCap, setRentCap] = useState("")
  const [noticePeriod, setNoticePeriod] = useState("60")

  const results = useMemo(() => {
    const rent = parseFloat(currentRent)
    const val = parseFloat(increaseValue)
    if (!rent || !val) return null

    let newRent: number
    if (increaseType === "cpi" || increaseType === "pct") {
      newRent = rent * (1 + val / 100)
    } else {
      newRent = rent + val
    }

    const increaseAmount = newRent - rent
    const pctIncrease = (increaseAmount / rent) * 100
    const cap = parseFloat(rentCap)
    const overCap = cap > 0 && pctIncrease > cap

    return { newRent, increaseAmount, pctIncrease, overCap, cap }
  }, [currentRent, increaseType, increaseValue, rentCap])

  const fmt = (n: number) => `${currency}${n.toFixed(2)}`

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Panel */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Rent Details</p>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Current Monthly Rent</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                  <input
                    type="number"
                    min="0"
                    value={currentRent}
                    onChange={e => setCurrentRent(e.target.value)}
                    placeholder="e.g. 1200"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Increase Type</label>
                <select
                  value={increaseType}
                  onChange={e => setIncreaseType(e.target.value as IncreaseType)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="pct">Fixed Percentage (%)</option>
                  <option value="cpi">CPI / Inflation Rate (%)</option>
                  <option value="flat">Flat Amount ({currency})</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  {increaseType === "flat" ? "Increase Amount ({currency})" : "Increase Rate (%)"}
                </label>
                <div className="relative">
                  {increaseType !== "flat" && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                  )}
                  {increaseType === "flat" && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currency}</span>
                  )}
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={increaseValue}
                    onChange={e => setIncreaseValue(e.target.value)}
                    placeholder={increaseType === "flat" ? "e.g. 50" : "e.g. 3.5"}
                    className={`w-full rounded-lg border border-slate-200 bg-white ${increaseType === "flat" ? "pl-7" : "pl-3"} pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40`}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Local Rent Cap % (optional)</label>
                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={rentCap}
                    onChange={e => setRentCap(e.target.value)}
                    placeholder="e.g. 5"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Notice Period Required</label>
                <select
                  value={noticePeriod}
                  onChange={e => setNoticePeriod(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  {NOTICE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            {/* Result Panel */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results ? (
                <p className="text-slate-400 text-sm text-center py-10">Enter rent and increase details to see results</p>
              ) : (
                <>
                  {results.overCap && (
                    <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-700 font-medium">
                        This increase ({results.pctIncrease.toFixed(1)}%) exceeds your local rent cap of {results.cap}%
                      </p>
                    </div>
                  )}

                  <div className="text-center py-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">New Monthly Rent</p>
                    <p className="text-5xl font-extrabold text-primary">{fmt(results.newRent)}</p>
                  </div>

                  <div className="border-t border-primary/10 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Monthly Increase</span>
                      <span className="font-semibold text-green-600">+{fmt(results.increaseAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Annual Increase</span>
                      <span className="font-semibold text-green-600">+{fmt(results.increaseAmount * 12)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">% Increase</span>
                      <span className="font-semibold text-slate-900">{results.pctIncrease.toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-primary/10 pt-3">
                      <span className="text-slate-600">Notice Period</span>
                      <span className="font-semibold text-slate-900">{noticePeriod} days</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Fixed % increase: New Rent = Current Rent × (1 + Rate ÷ 100)\nFlat increase: New Rent = Current Rent + Flat Amount\n\n% Increase = (New Rent − Old Rent) ÷ Old Rent × 100`}
        description="CPI-based increases link rent to the Consumer Price Index — use the current official CPI figure as your rate."
      />

      <ToolFAQ
        items={[
          {
            q: "How much can I increase rent by in the UK?",
            a: "In England, landlords can increase rent once per year for periodic tenancies. The increase must be 'fair and realistic' and in line with local market rates. Scotland has additional rent caps in place. Always serve the correct notice period before any increase takes effect.",
          },
          {
            q: "What notice do I need to give for a rent increase?",
            a: "In England, landlords must give at least one month's notice for monthly periodic tenancies, or six months for yearly tenancies. The notice must be in writing, usually via a Section 13 notice. Always check current legislation as rules change.",
          },
          {
            q: "Can I link rent increases to CPI?",
            a: "Yes. Many landlords and letting agents include a CPI-linked rent review clause in leases. This automatically adjusts rent each year in line with the Consumer Price Index, providing predictability for both parties. Use the official ONS figure for accuracy.",
          },
        ]}
      />

      <CTABox
        icon={ArrowUpRight}
        heading="Lease reviews coming up?"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
